// src/components/viewer/Filmstrip.tsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { PhotoRecord } from '../../types/domain';
import { storageService } from '../../services/storage';
import { getCategoryStyle } from './exifFormatter';
import { Image, ChevronDown, ChevronUp, Film } from 'lucide-react';

export interface FilmstripProps {
  photos: PhotoRecord[];
  currentIndex: number;
  onSelectPhoto: (index: number) => void;
  windowSize?: number; // e.g. 8 (showing index +- 8, default 8 = 17 adjacent photos)
  className?: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

/**
 * Individual lazy-loaded thumbnail item in the filmstrip.
 */
interface FilmstripItemProps {
  photo: PhotoRecord;
  globalIndex: number;
  isActive: boolean;
  onSelect: (index: number) => void;
}

const FilmstripItem: React.FC<FilmstripItemProps> = React.memo(
  ({ photo, globalIndex, isActive, onSelect }) => {
    const [thumbUrl, setThumbUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const itemRef = useRef<HTMLButtonElement | null>(null);

    // Auto-scroll the active item into horizontal center
    useEffect(() => {
      if (isActive && itemRef.current && typeof itemRef.current.scrollIntoView === 'function') {
        itemRef.current.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest',
        });
      }
    }, [isActive]);

    // Asynchronously resolve thumbnail URL from OPFS / IndexedDB / DataURL
    useEffect(() => {
      let isMounted = true;
      setIsLoading(true);

      async function loadThumb() {
        if (!photo.thumbnailKey) {
          if (isMounted) setIsLoading(false);
          return;
        }

        // If thumbnailKey is already a data URL or blob URL (e.g. sampleTrip demo)
        if (
          photo.thumbnailKey.startsWith('data:') ||
          photo.thumbnailKey.startsWith('blob:') ||
          photo.thumbnailKey.startsWith('http')
        ) {
          if (isMounted) {
            setThumbUrl(photo.thumbnailKey);
            setIsLoading(false);
          }
          return;
        }

        try {
          const url = await storageService.getPhotoUrl(photo.thumbnailKey);
          if (isMounted) {
            setThumbUrl(url || null);
            setIsLoading(false);
          }
        } catch {
          if (isMounted) setIsLoading(false);
        }
      }

      loadThumb();

      return () => {
        isMounted = false;
      };
    }, [photo.thumbnailKey]);

    const categoryStyle = useMemo(
      () => getCategoryStyle(photo.category),
      [photo.category]
    );

    return (
      <button
        ref={itemRef}
        type="button"
        role="tab"
        aria-selected={isActive}
        aria-label={`Vai alla foto ${globalIndex + 1} di ${photo.category || ''}`}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(globalIndex);
        }}
        className={`relative shrink-0 rounded-xl overflow-hidden transition-all duration-200 cursor-pointer select-none ${
          isActive
            ? 'w-16 h-16 sm:w-18 sm:h-18 ring-2 ring-sky-400 ring-offset-2 ring-offset-slate-950 scale-105 shadow-lg shadow-sky-500/30 z-10 opacity-100'
            : 'w-14 h-14 sm:w-16 sm:h-16 opacity-60 hover:opacity-100 hover:scale-[1.02] border border-slate-700/60'
        }`}
      >
        {/* Thumbnail Image */}
        {thumbUrl ? (
          <img
            src={thumbUrl}
            alt={photo.fileName}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            loading="lazy"
          />
        ) : isLoading ? (
          <div className="w-full h-full bg-slate-800 animate-pulse flex items-center justify-center">
            <Image className="w-4 h-4 text-slate-600 animate-spin" />
          </div>
        ) : (
          <div className="w-full h-full bg-slate-900 flex items-center justify-center text-slate-600">
            <Image className="w-4 h-4" />
          </div>
        )}

        {/* Category Accent Dot at Top-Right */}
        <span
          className="absolute top-1 right-1 w-2 h-2 rounded-full shadow-sm"
          style={{ backgroundColor: categoryStyle.dotColor }}
          title={photo.category}
        />

        {/* Global Photo Index Badge at Bottom-Left */}
        <span className="absolute bottom-1 left-1 px-1 py-0.2 text-[9px] font-mono font-bold bg-slate-950/80 backdrop-blur-sm text-slate-300 rounded shadow">
          {globalIndex + 1}
        </span>
      </button>
    );
  }
);

FilmstripItem.displayName = 'FilmstripItem';

export const Filmstrip: React.FC<FilmstripProps> = ({
  photos,
  currentIndex,
  onSelectPhoto,
  windowSize = 8,
  className = '',
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Compute chronologically adjacent window: +- windowSize photos
  const { visibleItems, hasPreceding, hasFollowing } = useMemo(() => {
    if (!photos || photos.length === 0) {
      return { visibleItems: [], hasPreceding: false, hasFollowing: false };
    }

    const startIndex = Math.max(0, currentIndex - windowSize);
    const endIndex = Math.min(photos.length - 1, currentIndex + windowSize);

    const items: Array<{ photo: PhotoRecord; globalIndex: number }> = [];
    for (let i = startIndex; i <= endIndex; i++) {
      items.push({
        photo: photos[i],
        globalIndex: i,
      });
    }

    return {
      visibleItems: items,
      hasPreceding: startIndex > 0,
      hasFollowing: endIndex < photos.length - 1,
    };
  }, [photos, currentIndex, windowSize]);

  if (!photos || photos.length === 0) return null;

  return (
    <div
      onClick={(e) => e.stopPropagation()} // Prevent PhotoSwipe tap actions
      className={`pointer-events-auto transition-all duration-300 z-30 select-none ${className}`}
    >
      {/* Collapsed Pill Floating Toggle */}
      {isCollapsed ? (
        <button
          type="button"
          onClick={onToggleCollapse}
          className="mx-auto flex items-center gap-2 px-3.5 py-1.5 bg-slate-900/80 hover:bg-slate-850 text-slate-300 hover:text-white rounded-full text-xs font-semibold backdrop-blur-md border border-slate-700/80 shadow-2xl transition cursor-pointer"
        >
          <Film className="w-3.5 h-3.5 text-sky-400" />
          <span>Mostra Sequenza ({currentIndex + 1} di {photos.length})</span>
          <ChevronUp className="w-3.5 h-3.5" />
        </button>
      ) : (
        <div className="relative w-full max-w-3xl mx-auto px-3 sm:px-4 py-2 bg-slate-950/80 backdrop-blur-md border border-slate-800/80 rounded-2xl shadow-2xl">
          {/* Ribbon Header with Collapse Toggle */}
          <div className="flex items-center justify-between pb-1.5 px-1 text-[11px] text-slate-400">
            <div className="flex items-center gap-1.5">
              <Film className="w-3.5 h-3.5 text-sky-400" />
              <span className="font-semibold text-slate-200">Foto Adiacenti</span>
              <span className="text-slate-500">•</span>
              <span className="font-mono text-slate-400">
                {currentIndex + 1} / {photos.length}
              </span>
            </div>

            {onToggleCollapse && (
              <button
                type="button"
                onClick={onToggleCollapse}
                className="flex items-center gap-1 hover:text-white transition cursor-pointer"
                aria-label="Minimizza sequenza foto"
              >
                <span>Nascondi</span>
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Horizontal Scrolling Track */}
          <div className="relative flex items-center">
            {/* Left Edge Gradient Fade */}
            {hasPreceding && (
              <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-slate-950/90 to-transparent z-10 flex items-center">
                <span className="text-[10px] font-mono text-slate-400 pl-1">«</span>
              </div>
            )}

            <div
              ref={containerRef}
              className="flex items-center gap-2 overflow-x-auto scroll-smooth py-1 px-1 scrollbar-none touch-pan-x w-full"
            >
              {visibleItems.map(({ photo, globalIndex }) => (
                <FilmstripItem
                  key={photo.id || globalIndex}
                  photo={photo}
                  globalIndex={globalIndex}
                  isActive={globalIndex === currentIndex}
                  onSelect={onSelectPhoto}
                />
              ))}
            </div>

            {/* Right Edge Gradient Fade */}
            {hasFollowing && (
              <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-slate-950/90 to-transparent z-10 flex items-center justify-end">
                <span className="text-[10px] font-mono text-slate-400 pr-1">»</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Filmstrip;
